// Import React Libraries
import React, { useEffect } from "react";

// Import Css File
import { IonCard, IonCardTitle, IonIcon, IonLoading, useIonViewDidLeave } from "@ionic/react";
import { checkmarkCircle } from "ionicons/icons";
import MaxWidthContainer from "../../../components/UI/MaxWidthContainer/MaxWidthContainer";
import { useConfig } from "../../../providers/config";
import { useOrderData } from "../../../providers/orderDataStore";
import Page from "../../page-layout";
import { useHistory, useParams } from "react-router";
import useGetCurrentAddress from "../../../hooks/useGetCurrentAddress";
import { getFullAddressForAddressType } from "../../../utils";
import { useTranslation } from "react-i18next";
import { useGetOrderByIdQuery } from "../../../providers/Orders";

const ThankYouPageSkeleton = () => {
  return (
    <>
      {/* Icon placeholder */}
      <div className="flex justify-center mt-2.5">
        <div className="w-14 h-14 rounded-full bg-gray-200 animate-pulse" />
      </div>

      <div className="flex flex-col gap-1 items-center p-4">
        {/* Title */}
        <div className="h-6 w-32 bg-gray-200 animate-pulse rounded mb-2" />

        {/* Subtitle */}
        <div className="h-5 w-48 bg-gray-200 animate-pulse rounded mb-4" />

        {/* Order details */}
        <div className="flex flex-col gap-2 w-full items-center">
          <div className="h-4 w-40 bg-gray-200 animate-pulse rounded" />
          <div className="h-4 w-36 bg-gray-200 animate-pulse rounded" />
          <div className="h-4 w-44 bg-gray-200 animate-pulse rounded" />
          <div className="h-4 w-56 bg-gray-200 animate-pulse rounded" />
        </div>

        {/* Contact info */}
        <div className="h-4 w-48 bg-gray-200 animate-pulse rounded mt-4" />
      </div>

      {/* Buttons */}
      <div className="flex flex-col gap-2 p-2.5 mt-2">
        <div className="h-10 w-full bg-gray-200 animate-pulse rounded-2xl" />
        <div className="h-10 w-full bg-gray-200 animate-pulse rounded-xl" />
      </div>
    </>
  );
};

const ThankYouPage = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const { reset: resetOrder } = useOrderData();
  const config = useConfig();
  const { orderId } = useParams<{ orderId?: string }>();
  const { data: orderData, isLoading: isLoadingOrderData } = useGetOrderByIdQuery(orderId ?? "", {
    enabled: !!orderId,
  });

  const address = useGetCurrentAddress();

  const handleMyOrders = () => {
    history.replace("/my-orders");
  };

  const handleContinueShopping = () => {
    history.replace("/home3");
  };

  const currentFullAddress = address ? getFullAddressForAddressType(address) : "";

  useEffect(() => {
    resetOrder();
  }, []);

  return (
    <Page>
      <Page.Content className="">
        <MaxWidthContainer
          classList={"thank-you global-content, pt-[env(safe-area-inset-top)]"}
          maxWidthBreakpoint={"Md"}
        >
          <IonCard className="ion-text-center">
            {isLoadingOrderData ? (
              <ThankYouPageSkeleton />
            ) : (
              <>
                <IonIcon className=" mt-2.5 text-[3.5rem] text-primary" icon={checkmarkCircle} />
                <div className="flex flex-col gap-1 ">
                  <IonCardTitle>{t("Thank You")}</IonCardTitle>
                  <h2 className="text-lg font-extrabold">Ваш заказ получен:</h2>
                  <div className="flex flex-col gap-1">
                    {orderId && (
                      <div>
                        {t("Orders ID")}: {orderId}
                      </div>
                    )}
                    <div>
                      {t("Delivery date")}: {orderData?.deliveryDate ? orderData.deliveryDate : "-"}
                    </div>
                    <div>
                      {t("Delivery interval")}: {orderData?.isExpressDelivery ? t("Express") : orderData?.deliveryTime}
                    </div>
                    <div>
                      {t("Address")}: {address?.fullAddress || currentFullAddress}
                    </div>
                  </div>
                  <div className="flex flex-col items-center">Свяжитесь с нами пo: {config.companyPhoneNumber} </div>
                </div>

                <div className="flex flex-col gap-1 p-2.5">
                  <button className="p-2.5 rounded-2xl bg-primary" onClick={handleMyOrders}>
                    {t("My Orders")}
                  </button>
                  <button onClick={handleContinueShopping} className="p-2.5">
                    {t("Continue Shopping")}
                  </button>
                </div>
              </>
            )}
          </IonCard>
        </MaxWidthContainer>
      </Page.Content>
    </Page>
  );
};

export default ThankYouPage;
